// Auction gallery editor using the existing resumable upload/progress hook.
// Uploads uniquely named files under the approved event items path; the audited
// callable owns gallery metadata (and linked Entry gallery synchronization).
// Removes metadata before deleting only files created for this auction item.
import React, { useState } from "react";
import { ref, deleteObject } from "firebase/storage";
import { httpsCallable } from "firebase/functions";
import { storage, functions } from "../firebase";
import { useUploadWithProgress } from "../hooks/use_upload_with_progress";

export default function AuctionImageEditor({ eventId, item, images, disabled, onImages, onBusy, notify }) {
  const { uploadFileWithProgress, progress } = useUploadWithProgress();
  const [upload, setUpload] = useState(null);
  const [removing, setRemoving] = useState("");
  const prefix = `items/${eventId}/auction-${item.id}-`;
  const manage = (action, imageUrl) => httpsCallable(functions, "manageAuctionItem")({ eventId, auctionItemId: item.id, action, imageUrl });

  const addFiles = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    if (!files.length) return;
    if (files.some((file) => !/^image\/(jpeg|png|webp|gif)$/.test(file.type) || file.size >= 5 * 1024 * 1024)) {
      notify("error", "Choose JPG, PNG, WebP, or GIF images smaller than 5 MB each.");
      return;
    }
    onBusy(true);
    try {
      for (let index = 0; index < files.length; index += 1) {
        setUpload({ index, total: files.length });
        const extension = files[index].type.split("/")[1].replace("jpeg", "jpg");
        const objectRef = ref(storage, `${prefix}${crypto.randomUUID()}.${extension}`);
        try {
          const url = await uploadFileWithProgress(objectRef, files[index]);
          const result = await manage("add_image", url);
          onImages(result.data.imageUrls);
        } catch (error) {
          // A failed metadata save must not leave an unreferenced upload.
          await deleteObject(objectRef).catch(() => {});
          throw error;
        }
      }
      notify("success", "Auction images uploaded.");
    } catch (error) {
      notify("error", error.message || "Unable to upload auction images.");
    } finally {
      setUpload(null);
      onBusy(false);
    }
  };
  const removeImage = async (url) => {
    if (!window.confirm("Remove this image from the auction gallery?")) return;
    onBusy(true);
    setRemoving(url);
    try {
      const result = await manage("remove_image", url);
      onImages(result.data.imageUrls);
      // Bundled demo assets and shared voting Entry files are not ours to erase.
      if (url.includes("/o/")) {
        const objectRef = ref(storage, url);
        if (objectRef.fullPath.startsWith(prefix)) {
          await deleteObject(objectRef).catch((error) => {
            if (error.code !== "storage/object-not-found") console.warn("Auction image removed; file cleanup failed", { code: error.code });
          });
        }
      }
      notify("success", "Auction image removed.");
    } catch (error) {
      notify("error", error.message || "Unable to remove auction image.");
    } finally {
      setRemoving("");
      onBusy(false);
    }
  };
  const totalProgress = upload ? Math.round(((upload.index + progress / 100) / upload.total) * 100) : 0;

  return <div className="mb-3">
    <label className="form-label" htmlFor={`images-${item.id}`}>Auction Images</label>
    {!disabled && <input id={`images-${item.id}`} type="file" className="form-control" accept="image/jpeg,image/png,image/webp,image/gif" multiple disabled={!!upload || !!removing} onChange={addFiles} />}
    {!disabled && item.linkedEntryId && <div className="form-text">These images also update the linked voting Entry gallery.</div>}
    {upload && <div className="mt-2">
      <div className="small">Uploading image {upload.index + 1} of {upload.total}</div>
      <div className="progress" role="progressbar" aria-label="Image upload progress" aria-valuenow={totalProgress} aria-valuemin={0} aria-valuemax={100}>
        <div className="progress-bar" style={{ width: `${totalProgress}%` }}>{totalProgress}%</div>
      </div>
    </div>}
    <div className="d-flex flex-wrap gap-2 mt-2">
      {images.map((url, index) => <div key={url} className="border rounded p-2">
        <img src={url} alt={`${item.title || "Auction item"} ${index + 1}`} style={{ width: 110, height: 90, objectFit: "cover" }} />
        {!disabled && <button className="btn btn-sm btn-outline-danger d-block mt-1" type="button" disabled={!!upload || !!removing} onClick={() => removeImage(url)}>{removing === url ? "Removing..." : "Delete image"}</button>}
      </div>)}
    </div>
    {!images.length && <div className="small text-muted mt-2">No auction images yet.</div>}
  </div>;
}
